import React from "react";
import { Link } from "react-router-dom";
import { FaMapMarkerAlt, FaEdit } from "react-icons/fa";
import dayjs from "dayjs";
import { useT } from "../contexts/TranslationContext";
import Card from "./ui/Card";
import ProjectStats from "./ProjectStats";

/**
 * Helper: inject Cloudinary transformation for thumbnails
 */
const injectTransformation = (originalUrl, transformation) => {
  if (!originalUrl) return originalUrl;
  try {
    const idx = originalUrl.indexOf("/upload/");
    if (idx === -1) return originalUrl;
    const before = originalUrl.substring(0, idx + 8); // include 'upload/'
    const after = originalUrl.substring(idx + 8);
    return before + transformation + after;
  } catch (e) {
    return originalUrl;
  }
};

const getCoverImageUrl = (url) => {
  if (!url) return null;
  // Use a larger thumbnail for cover images: 600x300 (2:1 aspect ratio)
  return injectTransformation(url, "c_fill,g_auto,w_600,h_300,q_auto,f_auto/");
};

/**
 * ProjectCard - Enhanced project card with cover image, statistics, and improved design
 */
const ProjectCard = ({ project, user, mapsHref, formatAddressCity, getStatusBadge }) => {
  const { t } = useT();
  const coverImageUrl = project.stats?.latestPhotoUrl
    ? getCoverImageUrl(project.stats.latestPhotoUrl)
    : null;

  return (
    <Card className="overflow-hidden hover:shadow-xl transition-all duration-300 p-0 h-full flex flex-col relative">
      {/* Cover Image - Clickable */}
      <Link
        to={`/projects/${project._id}`}
        className="relative h-48 bg-gradient-to-br from-gray-100 to-gray-200 overflow-hidden group block"
      >
        {coverImageUrl ? (
          <img
            src={coverImageUrl}
            alt={project.name}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
            loading="lazy"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <div className="text-gray-400 text-4xl font-bold opacity-30">
              {project.name?.charAt(0)?.toUpperCase() || "P"}
            </div>
          </div>
        )}
        {/* Status Badge Overlay */}
        <div className="absolute top-3 right-3 z-10 pointer-events-none">
          {getStatusBadge(project)}
        </div>
      </Link>
      
      {/* Edit Button - Only for admins, positioned over cover image */}
      {user && user.isAdmin && (
        <Link
          to={`/projects/${project._id}/edit`}
          className="absolute top-3 left-3 z-20 bg-white rounded-full p-2 shadow-md hover:shadow-lg hover:bg-gray-50 transition-all duration-200 flex items-center justify-center"
          title={t("projects.editProject")}
          onClick={(e) => e.stopPropagation()}
        >
          <FaEdit className="w-4 h-4 text-gray-700" />
        </Link>
      )}

      {/* Card Content */}
      <div className="p-4 flex-1 flex flex-col">
        {/* Project Name */}
        <Link
          to={`/projects/${project._id}`}
          className="block mb-2 group"
        >
          <h3 className="text-xl font-semibold text-gray-900 group-hover:text-brand transition-colors line-clamp-2">
            {project.name}
          </h3>
        </Link>

        {/* Address */}
        <div className="mb-3">
          {project.address || project.city ? (
            <a
              href={mapsHref(project)}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-1.5 text-sm text-gray-600 hover:text-brand transition-colors"
            >
              <FaMapMarkerAlt className="w-4 h-4 flex-shrink-0" />
              <span className="line-clamp-1">{formatAddressCity(project)}</span>
            </a>
          ) : (
            <div className="text-sm text-gray-400">{t("projects.noAddress")}</div>
          )}
        </div>

        {/* Statistics */}
        {project.stats && (
          <div className="mb-4 pt-3 border-t border-gray-100">
            <ProjectStats stats={project.stats} variant="compact" />
          </div>
        )}

        {/* Date Range (if available) - ordinal day + full month + year, localized via dayjs */}
        {(project.startDate || project.endDate) && (
          <div className="mb-3 text-xs text-gray-500">
            {project.startDate && (
              <span>
                {t("projects.start")}: {dayjs(project.startDate).format("Do MMMM YYYY")}
              </span>
            )}
            {project.startDate && project.endDate && " • "}
            {project.endDate && (
              <span>
                {t("projects.end")}: {dayjs(project.endDate).format("Do MMMM YYYY")}
              </span>
            )}
          </div>
        )}
      </div>
    </Card>
  );
};

export default ProjectCard;
